import { apiClient } from './client';

// ==================== TYPES ====================

export interface AnalyticsEvent {
    id: string;
    type: string;
    category: string;
    userId?: string;
    anonymousId?: string;
    sessionId?: string;
    properties: Record<string, any>;
    context?: Record<string, any>;
    timestamp: string;
    processedAt?: string;
}

export interface EventSummary {
    type: string;
    count: number;
}

export interface TimelineData {
    date: string;
    [eventType: string]: number | string;
}

export interface DashboardMetrics {
    period: { startDate: string; endDate: string; days: number };
    metrics: {
        signups: { current: number; previous: number; change: number };
        logins: { current: number; previous: number; change: number };
        boardsCreated: { current: number; previous: number; change: number };
        revenue: { current: number; previous: number; change: number };
    };
    appStats?: {
        users: { 
            total: number;
            locations?: { country: string; count: number }[];
        };
        boards: { total: number };
        journals: { total: number; created: number; updated: number; deleted: number; };
        operations: { total: number; created: number; updated: number; deleted: number; };
        channels: { 
            total: number;
            distribution: { type: string; count: number }[];
            connected: number;
            updated: number;
            disconnected: number;
        };
        ai: {
            totalTokens: number;
            estimatedCost: number;
            requestCount: number;
            activeUsers: number;
            avgTokensPerUser: number;
        };
        email: {
            totalSent: number;
            totalOpened: number;
            totalClicked: number;
        };
        sync: {
            totalTransactions: number;
        };
        security: {
            recentFailures: number;
        };
        visitorStats?: {
            topLocations: { country: string; count: number }[];
        };
        featureUsage?: {
            pageStats: { type: string; userCount: number; boardCount: number }[];
            chartStats: { type: string; count: number }[];
        };
    };
    // Legacy mapping for DashboardPage compatibility
    signups?: { total: number; change: number };
    logins?: { total: number; change: number };
    activeUsers?: { total: number; change: number };
    revenue?: { total: number; change: number };
    newSubscriptions?: { total: number; change: number };
    canceledSubscriptions?: { total: number; change: number };
    boards?: { total: number; change: number };
    channels?: { total: number; change: number };
}

export interface CategoryBreakdown {
    category: string;
    count: number;
    percentage: number;
}

export interface UserActivity {
    userId: string;
    eventCount: number;
    lastActivity: string;
    eventBreakdown: Record<string, number>;
}

export interface DailyMetric {
    id: string;
    metricName: string;
    date: string;
    value: number;
}

export interface MetricSummary {
    metricName: string;
    period: { start: string; end: string; days: number };
    total: number;
    average: number;
    dataPoints: { date: string; value: number }[];
}

export interface QueueStatus {
    initialized: boolean;
    eventQueue?: {
        waiting: number;
        active: number;
        completed: number;
        failed: number;
        delayed: number;
    };
    aggregationQueue?: {
        waiting: number;
        active: number;
        completed: number;
        failed: number;
        delayed: number;
    };
}

export interface Trigger {
    id: string;
    name: string;
    description?: string;
    eventType: string;
    conditions: Record<string, any>;
    actions: Record<string, any>[];
    isActive: boolean;
    priority: number;
    executionCount: number;
    lastExecutedAt?: string;
    createdAt: string;
    updatedAt: string;
}

// ==================== ANALYTICS API ====================

export const analyticsApi = {
    /**
     * Get events with filtering and pagination
     */
    async getEvents(params: {
        userId?: string;
        category?: string;
        type?: string;
        startDate?: string;
        endDate?: string;
        limit?: number;
        offset?: number;
        orderBy?: string;
        orderDir?: 'asc' | 'desc';
    }) {
        const response = await apiClient.get('/admin/analytics/events', { params });
        return response.data.data as AnalyticsEvent[];
    },

    /**
     * Get event summary (counts by type)
     */
    async getSummary(params?: {
        startDate?: string;
        endDate?: string;
        userId?: string;
    }) {
        const response = await apiClient.get('/admin/analytics/summary', { params });
        return response.data.data as EventSummary[];
    },

    /**
     * Get event timeline (daily counts)
     */
    async getTimeline(params?: {
        startDate?: string;
        endDate?: string;
        eventTypes?: string;
    }) {
        const response = await apiClient.get('/admin/analytics/timeline', { params });
        return response.data.data as TimelineData[];
    },

    /**
     * Get dashboard metrics
     */
    async getDashboardMetrics(days: number = 30) {
        const response = await apiClient.get('/admin/analytics/dashboard', { params: { days } });
        return response.data.data as DashboardMetrics;
    },

    /**
     * Get user activity
     */
    async getUserActivity(userId: string, days: number = 30) {
        const response = await apiClient.get(`/admin/analytics/user/${userId}`, { params: { days } });
        return response.data.data as UserActivity;
    },

    /**
     * Get category breakdown
     */
    async getCategoryBreakdown(params?: {
        startDate?: string;
        endDate?: string;
    }) {
        const response = await apiClient.get('/admin/analytics/categories', { params });
        return response.data.data as CategoryBreakdown[];
    },

    /**
     * Get daily metrics
     */
    async getMetrics(params?: {
        metricName?: string;
        startDate?: string;
        endDate?: string;
    }) {
        const response = await apiClient.get('/admin/analytics/metrics', { params });
        return response.data.data as DailyMetric[];
    },

    /**
     * Get event types
     */
    async getEventTypes() {
        const response = await apiClient.get('/admin/analytics/event-types');
        return response.data.data as {
            eventTypes: { key: string; value: string; category: string }[];
            categories: { key: string; value: string }[];
        };
    },

    // ============ AGGREGATION ENDPOINTS ============

    /**
     * Trigger daily aggregation
     */
    async runAggregation(date?: string) {
        const response = await apiClient.post('/admin/analytics/aggregation/run', { date });
        return response.data;
    },

    /**
     * Backfill metrics
     */
    async backfillMetrics(startDate: string, endDate: string) {
        const response = await apiClient.post('/admin/analytics/aggregation/backfill', { startDate, endDate });
        return response.data;
    },

    /**
     * Get metric summary
     */
    async getMetricSummary(metricName: string, days: number = 30) {
        const response = await apiClient.get(`/admin/analytics/aggregation/metric/${metricName}`, { params: { days } });
        return response.data.data as MetricSummary;
    },

    /**
     * Get available metrics
     */
    async getAvailableMetrics() {
        const response = await apiClient.get('/admin/analytics/aggregation/available-metrics');
        return response.data.data as string[];
    },

    /**
     * Get aggregation status
     */
    async getAggregationStatus() {
        const response = await apiClient.get('/admin/analytics/aggregation/status');
        return response.data.data as QueueStatus;
    },
};

// ==================== TRIGGERS API ====================

export const triggersApi = {
    /**
     * Get all triggers
     */
    async getTriggers(params?: { isActive?: boolean }) {
        const response = await apiClient.get('/admin/triggers', { params });
        return response.data.data as Trigger[];
    },

    /**
     * Get trigger by ID
     */
    async getTrigger(id: string) {
        const response = await apiClient.get(`/admin/triggers/${id}`);
        return response.data.data as Trigger;
    },

    /**
     * Create trigger
     */
    async createTrigger(trigger: Partial<Trigger>) {
        const response = await apiClient.post('/admin/triggers', trigger);
        return response.data.data as Trigger;
    },

    /**
     * Update trigger
     */
    async updateTrigger(id: string, trigger: Partial<Trigger>) {
        const response = await apiClient.put(`/admin/triggers/${id}`, trigger);
        return response.data.data as Trigger;
    },

    /**
     * Delete trigger
     */
    async deleteTrigger(id: string) {
        const response = await apiClient.delete(`/admin/triggers/${id}`);
        return response.data;
    },

    /**
     * Toggle trigger active state
     */
    async toggleTrigger(id: string) {
        const response = await apiClient.patch(`/admin/triggers/${id}/toggle`);
        return response.data.data as Trigger;
    },

    /**
     * Get trigger executions
     */
    async getTriggerExecutions(id: string, limit: number = 50) {
        const response = await apiClient.get(`/admin/triggers/${id}/executions`, { params: { limit } });
        return response.data.data;
    },

    /**
     * Test trigger
     */
    async testTrigger(id: string, sampleEvent: Record<string, any>) {
        const response = await apiClient.post(`/admin/triggers/${id}/test`, { sampleEvent });
        return response.data;
    },

    /**
     * Get trigger stats
     */
    async getTriggerStats() {
        const response = await apiClient.get('/admin/triggers/stats/summary');
        return response.data.data;
    },

    /**
     * Initialize default triggers
     */
    async initializeDefaults() {
        const response = await apiClient.post('/admin/triggers/initialize-defaults');
        return response.data;
    },

    /**
     * Get trigger options
     */
    async getTriggerOptions() {
        const response = await apiClient.get('/admin/triggers/options/all');
        return response.data.data;
    },

    /**
     * Get queue status
     */
    async getQueueStatus() {
        const response = await apiClient.get('/admin/triggers/queue/status');
        return response.data.data;
    },
};
